<template>
  <div
    ref="el"
    :style="{
      width: '100%',
      height: typeof height === 'number' ? `${height}px` : height
    }"
  />
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, shallowRef, watch, computed, defineProps, defineEmits } from 'vue'
import * as echarts from 'echarts'

const props = defineProps({
  rows: { type: Array, default: () => [] },

  height: { type: [Number, String], default: 500 },
  visibleCount: { type: Number, default: 8 },

  colorRange: { type: Array, default: () => ['#EDF5F1', '#0EE378', '#000D1C'] },

  wrapLabel: { type: Function, default: null },

  maxLabelLines: { type: Number, default: 2 },
  labelLineHeight: { type: Number, default: 14 }
})

const emit = defineEmits(['bar-click'])

const el = ref(null)
const chart = shallowRef(null)
let ro = null

const normalizedRows = computed(() => (Array.isArray(props.rows) ? props.rows : []))

const processosCount = computed(() => {
  const set = new Set()
  for (const r of normalizedRows.value) {
    const p = r?.processo
    if (p != null && p !== '') set.add(String(p))
  }
  return set.size
})

const hasZoom = computed(() => processosCount.value > props.visibleCount)

function calculateAxisParams(maxValue) {
  if (!maxValue || maxValue <= 0) return { max: 10, interval: 1 }

  const niceSteps = [
    1, 2, 5, 10, 20, 50, 100, 200, 500,
    1000, 2000, 5000, 10000, 20000, 50000,
    100000, 200000, 500000, 1000000, 2000000,
    5000000, 10000000
  ]

  let bestStep = 1
  for (let i = 0; i < niceSteps.length; i++) {
    if (niceSteps[i] * 10 >= maxValue) {
      bestStep = niceSteps[i]
      break
    }
  }

  const newMax = Math.ceil(maxValue / (bestStep * 10)) * (bestStep * 10)
  return { max: newMax, interval: newMax / 10 }
}

function buildModel() {
  const src = normalizedRows.value

  const clean = []
  for (const r of src) {
    const processo = r?.processo ?? 'N/A'
    const etapa = r?.etapa ?? 'N/A'

    const inscricoes = Number(r?.inscricoes ?? 0)
    const mediaRaw = Number(r?.media ?? 0)
    const media = Math.max(0, Math.min(100, Number.isFinite(mediaRaw) ? mediaRaw : 0))

    const idProcesso = r?.idProcesso ?? null
    const meta = r?.meta ?? null

    clean.push({
      processo: String(processo),
      etapa: String(etapa),
      inscricoes: Number.isFinite(inscricoes) ? Math.max(0, inscricoes) : 0,
      media,
      idProcesso,
      meta
    })
  }

  const totalByProcesso = new Map()
  const etapasInOrder = []
  const etapaSeen = new Set()

  for (const r of clean) {
    totalByProcesso.set(r.processo, (totalByProcesso.get(r.processo) || 0) + r.inscricoes)
    if (!etapaSeen.has(r.etapa)) {
      etapaSeen.add(r.etapa)
      etapasInOrder.push(r.etapa)
    }
  }

  const processos = Array.from(totalByProcesso.entries())
    .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]))
    .map(([p]) => p)

  const etapas = etapasInOrder.length ? etapasInOrder : ['N/A']

  const map = new Map()
  for (const r of clean) {
    map.set(`${r.processo}||${r.etapa}`, r)
  }

  const matrix = []
  let maxInscr = 0

  for (const processo of processos) {
    for (const etapa of etapas) {
      const key = `${processo}||${etapa}`
      const row = map.get(key) || {
        processo, etapa, inscricoes: 0, media: 0, idProcesso: null, meta: null
      }

      maxInscr = Math.max(maxInscr, Number(row.inscricoes || 0))

      matrix.push({
        processo,
        etapa,
        inscricoes: Number(row.inscricoes || 0),
        media: Number(row.media || 0),
        idProcesso: row.idProcesso ?? null,
        meta: row.meta ?? null
      })
    }
  }

  return { processos, etapas, matrix, maxInscr }
}

function buildOption() {
  const { processos, etapas, matrix, maxInscr } = buildModel()
  const { max, interval } = calculateAxisParams(maxInscr)

  const endValue = Math.min(props.visibleCount - 1, Math.max(processos.length - 1, 0))

  const visualMapReserve = 56
  const gridRight = 12 + visualMapReserve

  const labelReserve =
    16 + (props.labelLineHeight * Math.max(1, props.maxLabelLines)) + 14

  const sliderReserve = hasZoom.value ? 26 : 0
  const gridBottom = labelReserve + sliderReserve + 10

  const dataset = [
    { id: 'raw', source: matrix },
    ...etapas.map((etapa) => ({
      id: `ds-${etapa}`,
      fromDatasetId: 'raw',
      transform: {
        type: 'filter',
        config: { dimension: 'etapa', '=': etapa }
      }
    }))
  ]

  const series = etapas.map((etapa, idx) => ({
    name: etapa,
    type: 'bar',
    datasetIndex: idx + 1,
    barMaxWidth: 14,
    barGap: '12%',
    barCategoryGap: '35%',
    encode: {
      x: 'processo',
      y: 'inscricoes',
      tooltip: ['processo', 'etapa', 'inscricoes', 'media'],
      itemName: 'processo'
    },
    emphasis: { focus: 'series' }
  }))

  return {
    animation: true,

    dataset,

    grid: {
      left: 12,
      right: gridRight,
      top: 10,
      bottom: gridBottom,
      containLabel: true
    },

    tooltip: {
      trigger: 'item',
      confine: true,
      backgroundColor: '#fff',
      borderColor: '#C5C7C6',
      borderWidth: 1,
      padding: [8, 10],
      textStyle: { color: '#191C1C', fontSize: 12 },
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params
        const d = p?.data || {}

        const processo = d.processo ?? p?.name ?? 'Processo'
        const etapa = d.etapa ?? p?.seriesName ?? 'Etapa'
        const qtd = Number(d.inscricoes ?? 0)
        const media = Number(d.media ?? 0)

        return `
          <strong>${processo}</strong><br/>
          ${p.marker} <strong>${etapa}</strong><br/>
          Inscrições: ${qtd.toLocaleString('pt-BR')}<br/>
          Média: ${media.toFixed(2)}%
        `
      }
    },

    xAxis: {
      type: 'category',
      axisTick: { show: false, alignWithLabel: true },
      axisLine: { lineStyle: { color: '#C5C7C6' } },
      axisLabel: {
        interval: 0,
        margin: 12,
        fontSize: 11,
        formatter: (v) => (props.wrapLabel ? props.wrapLabel(v) : String(v))
      }
    },

    yAxis: {
      type: 'value',
      min: 0,
      max,
      interval,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { formatter: (v) => Number(v).toLocaleString('pt-BR'), fontSize: 11 },
      splitLine: { show: true, lineStyle: { color: '#EAEBEA' } }
    },

    visualMap: {
      type: 'continuous',
      orient: 'vertical',
      right: 10,
      top: 'center',
      min: 0,
      max: 100,
      dimension: 'media',
      seriesIndex: etapas.map((_, i) => i),
      formatter: (v) => `${Math.round(v)}%`,
      text: ['100%', '0%'],
      inRange: { color: props.colorRange },
      calculable: false,
      itemHeight: 180,
      itemWidth: 12
    },

    dataZoom: hasZoom.value
      ? [
          {
            type: 'inside',
            xAxisIndex: 0,
            zoomOnMouseWheel: false,
            moveOnMouseWheel: true,
            moveOnMouseMove: true,
            preventDefaultMouseMove: true,
            startValue: 0,
            endValue,
            filterMode: 'empty'
          },
          {
            type: 'slider',
            xAxisIndex: 0,
            bottom: 10,
            height: 10,
            showDetail: false,
            showDataShadow: false,
            brushSelect: false,
            zoomLock: true,
            startValue: 0,
            endValue,
            filterMode: 'empty',
            backgroundColor: {
              type: 'linear',
              x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [
                { offset: 0, color: '#f5f5f5' },
                { offset: 1, color: '#e1e1e1' }
              ]
            },
            borderWidth: 0,
            fillerColor: '#8E9191',

            handleSize: '0%',
            handleStyle: { color: 'transparent', borderWidth: 0 },

            moveHandleSize: 0,
            moveHandleStyle: { color: 'transparent', borderWidth: 0 },

            borderRadius: 999
          }
        ]
      : [],

    series
  }
}

function render() {
  if (!chart.value) return
  const option = buildOption()
  chart.value.setOption(option, {
    notMerge: true,
    lazyUpdate: true,
    replaceMerge: ['dataset', 'series', 'visualMap', 'dataZoom', 'xAxis', 'yAxis', 'grid']
  })
}

onMounted(() => {
  if (!el.value) return
  chart.value = echarts.init(el.value)

  chart.value.on('click', (params) => {
    const d = params?.data
    if (d) emit('bar-click', d)
  })

  ro = new ResizeObserver(() => chart.value?.resize())
  ro.observe(el.value)

  render()
})

onBeforeUnmount(() => {
  if (ro && el.value) ro.unobserve(el.value)
  ro = null

  if (chart.value) {
    chart.value.dispose()
    chart.value = null
  }
})

watch(
  () => [props.rows, props.visibleCount, props.colorRange, props.wrapLabel],
  () => render(),
  { deep: true }
)
</script>
